import { semanasDelMes, diasSemanaEnMes } from "@/lib/gastoFijo";
import { weekBounds } from "@/lib/semanas";

const sk = (w) => `${w.year}-${w.week}`;
const iso = (t) => new Date(t).toISOString().slice(0, 10);
const num = (x) => (x == null ? null : Number(x));
const sum = (a) => a.reduce((s, x) => s + (x ?? 0), 0);

// Excel P. MENSUAL: semana real = dato del módulo; proyección = (mes − reales) ÷ días restantes × días de la semana.
function proyectada(mes, filas, realFn) {
  const esReal = (w) => w.real && realFn(w) != null;
  const reales = sum(filas.filter(esReal).map(realFn));
  const resto = sum(filas.filter((w) => !esReal(w)).map((w) => w.dias));
  return { mes, w: (w) => (esReal(w) ? realFn(w) : mes == null || !resto ? null : ((mes - reales) / resto) * w.dias) };
}

const ejVigente = (calc, w, ym) => {
  const desde = iso(Math.max(weekBounds(w.year, w.week)[0].getTime(), Date.UTC(ym.year, ym.month - 1, 1)));
  return [...calc].reverse().find((e) => e.inicio <= desde);
};

// Comparativo semanal: PLAN de la semana con METAS de Gestión Planta (venta = meta diaria × días; tasas de METAS).
export function planSemana(w, vars, ym) {
  const dm = new Date(ym.year, ym.month, 0).getDate();
  const meta = num(vars?.ventas?.meta);
  const fila = { ...w, real: true, kg: meta == null ? null : (meta / dm) * w.dias };
  const planVars = Object.fromEntries(Object.entries(vars || {}).map(([k, v]) => [k, { ...v, semanas: { [sk(w)]: v?.meta } }]));
  return { fila, planVars };
}

export function semanasProyeccion(ym, vars, precios) {
  const v = vars?.ventas;
  const p = Object.fromEntries((precios || []).map((x) => [sk(x), x]));
  const base = semanasDelMes(ym.year, ym.month).map((w) => {
    const real = v?.semanas?.[sk(w)];
    return { ...w, dias: diasSemanaEnMes(ym.year, ym.month, w.year, w.week), real: real != null, kgReal: num(real), pv: p[sk(w)]?.pv_kg ?? null, pc: p[sk(w)]?.pc_kg ?? null, flete: p[sk(w)]?.flete_kg ?? null };
  });
  const ventaMes = num(v?.actualizacion);
  const venta = proyectada(ventaMes, base, (w) => w.kgReal);
  return { filas: base.map((w) => ({ ...w, kg: venta.w(w) })), ventaMes };
}

export function filasProyeccion({ filas, ventaMes, ym, gp, gf, vars }) {
  const precio = (k, label) => {
    const w = (x) => (x[k] == null || x.kg == null ? null : x[k] * x.kg);
    return { k, label, w, mes: filas.some((x) => w(x) == null) ? null : sum(filas.map(w)) };
  };
  const aplic = (calc, k, label) => {
    const last = calc[calc.length - 1];
    const real = (w) => { const e = ejVigente(calc, w, ym); return e ? e.diaria[k] * w.dias : null; };
    return { k, label, ...proyectada(last ? last.mensual[k] : null, filas, real) };
  };
  const variable = (k, label, pct) => {
    const rate = (x) => (x == null ? null : Number(x) / (pct ? 100 : 1));
    const vv = vars?.[k];
    const mes = vv?.actualizacion != null && ventaMes != null ? rate(vv.actualizacion) * ventaMes : null;
    const real = (w) => (vv?.semanas?.[sk(w)] == null || w.kg == null ? null : rate(vv.semanas[sk(w)]) * w.kg);
    return { k, label, unit: pct ? "kg" : "$", ...proyectada(mes, filas, real) };
  };
  const total = (k, label, rows, extra) => ({ k, label, total: true, w: (w) => { const v = rows.map((r) => r.w(w)); return v.some((x) => x == null) ? null : sum(v); }, mes: rows.some((r) => r.mes == null) ? null : sum(rows.map((r) => r.mes)), ...extra });
  const pv = precio("pv", "P.V."), pc = precio("pc", "P.C."), fl = precio("flete", "FLETE");
  const mg = { k: "mg", label: "MARGEN DE DISTRIBUCIÓN", b: "B1", total: true,
    w: (w) => (pv.w(w) == null || pc.w(w) == null || fl.w(w) == null ? null : pv.w(w) - pc.w(w) - fl.w(w)),
    mes: pv.mes == null || pc.mes == null || fl.mes == null ? null : pv.mes - pc.mes - fl.mes };
  const hgnKg = variable("hgn", "HGN (kilos)", true);
  // Excel fila 21: HGN = (kilos HGN) × (P.C. + Flete) × -1; mes con P.C. y Flete promedio del mes.
  const costoKg = (w) => (w.pc == null || w.flete == null ? null : w.pc + w.flete);
  const hgnMes = hgnKg.mes == null || pc.mes == null || fl.mes == null || !ventaMes ? null : -hgnKg.mes * ((pc.mes + fl.mes) / ventaMes);
  const hgnReal = (w) => { const kg = hgnKg.w(w); return kg == null || costoKg(w) == null ? null : -kg * costoKg(w); };
  const hgn = { k: "hgn_imp", label: "HGN", ...proyectada(hgnMes, filas, hgnReal) };

  const gestionables = [aplic(gp, "presupuesto_planta", "PRESUPUESTO"), aplic(gp, "extraordinarios", "EXTRAORDINARIOS"), aplic(gp, "provisiones_planta", "PROVISIONES PLANTA"),
    variable("comisiones", "COMISIONES"), variable("descuentos", "DESCUENTOS"), hgn];
  const fijo = [aplic(gf, "corporativos", "CORPORATIVOS"), aplic(gf, "impuestos_federales", "IMPUESTOS FEDERALES"), aplic(gf, "costo_bancario", "COSTO BANCARIO"),
    aplic(gf, "deuda_bancaria", "DEUDA BANCARIA"), aplic(gf, "inversiones", "INVERSIONES")];
  const b2 = total("b2", "GASTOS GESTIONABLES", gestionables, { b: "B2" });
  const b3 = total("b3", "GASTO FIJO", fijo, { b: "B3" });
  const res = { k: "resultado", label: "RESULTADO", total: true,
    w: (w) => ([mg.w(w), b2.w(w), b3.w(w)].some((x) => x == null) ? null : mg.w(w) - b2.w(w) - b3.w(w)),
    mes: [mg.mes, b2.mes, b3.mes].some((x) => x == null) ? null : mg.mes - b2.mes - b3.mes };
  return [
    pv, pc, fl, mg, { gap: 1 },
    ...gestionables, hgnKg, b2, { gap: 2 },
    ...fijo, b3, { gap: 3 },
    res,
  ];
}
